import type { SubscriptionStore } from '@/services/subscriptionStore'

export type PurchaseRecord = Record<string, unknown>

/** Same entitlement rule used by purchase management; null expiration has no end date. */
export function isPurchaseInEffect(purchase: PurchaseRecord, now = Date.now()): boolean {
  const expiration = purchase.expiration
  if (expiration === null || expiration === undefined) return true
  const end = typeof expiration === 'string' ? Date.parse(expiration) : NaN
  return Number.isFinite(end) && end > now
}

export function purchaseSubscriptionId(purchase: PurchaseRecord): string | null {
  const id = purchase.original_transaction_id ?? purchase.originalTransactionId ?? purchase.paddle_subscription_id
  return typeof id === 'string' && id.trim() ? id.trim() : null
}

export function purchaseStoreId(purchase: PurchaseRecord): number | null {
  return typeof purchase.store === 'number' && Number.isSafeInteger(purchase.store) && purchase.store > 0
    ? purchase.store : null
}

export interface OfferSubscription {
  subscriptionId: string
  storeId: number
  expiresAt: number
}

export function isSubscriptionPurchase(purchase: PurchaseRecord, stores: Map<number, SubscriptionStore>): boolean {
  const storeId = purchaseStoreId(purchase)
  const category = storeId === null ? null : stores.get(storeId)?.categoryName
  return category === 'Subscriptions' || category === 'Subscriptions Super'
}

/** Never search old monthly purchases to override the currently selected subscription. */
export function selectLimitedTimeOfferSubscription(
  purchases: PurchaseRecord[],
  stores: Map<number, SubscriptionStore>,
  selectedSubscriptionId: string | null,
  monthlyPriceId: string,
  now = Date.now(),
): OfferSubscription | null {
  if (!selectedSubscriptionId || !monthlyPriceId) return null
  const active = purchases.filter(p => isPurchaseInEffect(p, now) && p.is_valid !== false)
  // Unknown active metadata cannot safely exclude a parallel yearly/unlimited plan.
  if (active.some(p => purchaseStoreId(p) === null || !stores.has(purchaseStoreId(p)!))) return null
  if (active.some(p => !['Subscriptions', 'Subscriptions Super', 'Additional Credits'].includes(stores.get(purchaseStoreId(p)!)!.categoryName ?? ''))) return null
  const subscriptions = active.filter(p => isSubscriptionPurchase(p, stores))
  if (subscriptions.some(p => stores.get(purchaseStoreId(p)!)!.period !== 30)) return null
  const selected = subscriptions.find(p => purchaseSubscriptionId(p) === selectedSubscriptionId)
  if (!selected || selected.is_valid !== true || selected.is_trial_period !== false ||
    selected.is_cancelled !== false || selected.auto_renew_status !== true) return null
  if (selected.platform !== 'paddle_billing' && selected.platform !== 'web') return null
  const storeId = purchaseStoreId(selected)!
  const metadata = stores.get(storeId)!
  if (metadata.categoryName !== 'Subscriptions' || metadata.period !== 30 || metadata.apple_id !== monthlyPriceId) return null
  const expiresAt = typeof selected.expiration === 'string' ? Date.parse(selected.expiration) : NaN
  // A recurring monthly purchase must have a real current-period end, not an unlimited grant.
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return null
  return { subscriptionId: selectedSubscriptionId, storeId, expiresAt }
}
